import { sharedWorkerMessageType as messageType } from './utils';

// The browser can kill the SharedWorker while the tab keeps running. A MessagePort gives
// the tab no signal for that, so the tab pings the worker and treats a run of unanswered pings as a
// dead worker. Defaults allow roughly 20s of silence before the tab is declared disconnected.
export const HEARTBEAT_INTERVAL_MS = 5000;
export const HEARTBEAT_MAX_MISSED = 4;
// A tick that arrives this many intervals late means the tab was frozen, intensively throttled or the
// machine slept. Missed pings from such a gap say nothing about the worker, so they are not counted.
export const HEARTBEAT_SUSPEND_FACTOR = 3;
// How long a one-off probe (another tab reported the worker dead) waits for a pong. Long enough that a worker
// which is only busy for a few seconds does not make healthy tabs reload (and lose in-progress edits), still
// well below the ~20 s silence the heartbeat itself needs.
export const HEARTBEAT_PROBE_TIMEOUT_MS = 10_000;

/**
 * Tab-side liveness check for the AIQ SharedWorker.
 * @param {Object} options
 * @param {MessagePort} options.port - Started port of the SharedWorker
 * @param {Function} options.onDead - Called once when the worker stops answering
 * @param {number} [options.intervalMs] - Time between pings
 * @param {number} [options.maxMissed] - Consecutive unanswered pings that mean the worker is dead
 * @param {Document} [options.doc] - Document used for visibility / resume events
 */
export function createSharedWorkerHeartbeat({
    port,
    onDead,
    intervalMs = HEARTBEAT_INTERVAL_MS,
    maxMissed = HEARTBEAT_MAX_MISSED,
    doc = globalThis.document,
}) {
    let running = false;
    let timerId = null;
    let seq = 0;
    let awaitingPong = false;
    let missed = 0;
    let lastTickAt = 0;
    let lastPongAt = 0;
    let probeCallbacks = [];

    const sendPing = () => {
        seq += 1;
        awaitingPong = true;
        port.postMessage({ type: messageType.heartbeatPing, data: { seq } });
    };

    const schedule = () => {
        clearTimeout(timerId);
        timerId = setTimeout(tick, intervalMs);
    };

    const declareDead = (now) => {
        console.warn(
            `[SharedWorkerHeartbeat] SharedWorker did not answer ${missed} pings, lastPongAgoMs=${now - lastPongAt}, hidden=${!!doc?.hidden}`
        );
        stop();
        onDead();
    };

    function tick() {
        if (!running) return;
        const now = Date.now();
        const suspended = now - lastTickAt > intervalMs * HEARTBEAT_SUSPEND_FACTOR;
        lastTickAt = now;
        if (suspended) missed = 0;
        else if (awaitingPong) missed += 1;
        if (missed >= maxMissed) {
            declareDead(now);
            return;
        }
        sendPing();
        schedule();
    }

    const onMessage = (e) => {
        if (e?.data?.type !== messageType.heartbeatPong) return;
        awaitingPong = false;
        missed = 0;
        lastPongAt = Date.now();
        settleProbes(true);
    };

    const settleProbes = (alive) => {
        const callbacks = probeCallbacks;
        probeCallbacks = [];
        callbacks.forEach((callback) => callback(alive));
    };

    /**
     * Sends one extra ping and resolves with whether the worker answered in time. Used when another tab
     * reports the worker dead, so a healthy tab does not reload on someone else's false alarm.
     * @param {number} [timeoutMs] - How long to wait for the pong
     * @returns {Promise<boolean>} true when a pong arrived, false on timeout or when the heartbeat is not running
     */
    function probe(timeoutMs = HEARTBEAT_PROBE_TIMEOUT_MS) {
        if (!running) return Promise.resolve(false);
        return new Promise((resolve) => {
            const timeoutId = setTimeout(() => {
                probeCallbacks = probeCallbacks.filter((callback) => callback !== settle);
                resolve(false);
            }, timeoutMs);
            const settle = (alive) => {
                clearTimeout(timeoutId);
                resolve(alive);
            };
            probeCallbacks.push(settle);
            sendPing();
        });
    }

    // Timers of a hidden or frozen tab may not have run on time, so restart the count from a fresh ping
    // instead of judging the worker on pings the tab could not have measured.
    const onWake = () => {
        if (!running || doc?.hidden) return;
        missed = 0;
        lastTickAt = Date.now();
        sendPing();
        schedule();
    };

    function start() {
        if (running) return;
        running = true;
        missed = 0;
        lastTickAt = lastPongAt = Date.now();
        port.addEventListener('message', onMessage);
        doc?.addEventListener('visibilitychange', onWake);
        doc?.addEventListener('resume', onWake);
        sendPing();
        schedule();
    }

    function stop() {
        running = false;
        clearTimeout(timerId);
        timerId = null;
        port.removeEventListener('message', onMessage);
        doc?.removeEventListener('visibilitychange', onWake);
        doc?.removeEventListener('resume', onWake);
        settleProbes(false);
    }

    return { start, stop, isRunning: () => running, probe };
}
